import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "button", "toggle", "form"]
  static values = { url: String, author: String }

  connect() {
    this.#observeMessages()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  expand() {
    this.toggleTarget.classList.add("hidden")
    this.formTarget.classList.remove("hidden")
    this.inputTarget.focus()
  }

  submitOnEnter(event) {
    if (event.shiftKey) return
    event.preventDefault()
    this.submit()
  }

  resize() {
    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = `${input.scrollHeight}px`
  }

  async submit() {
    const content = this.inputTarget.value.trim()
    if (!content) return

    this.inputTarget.value = ""
    this.resize()
    this.buttonTarget.disabled = true

    const optimistic = this.#appendOptimisticMessage(content)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: new URLSearchParams({ content })
      })

      if (!response.ok) {
        optimistic.remove()
        this.inputTarget.value = content
      }
    } catch {
      optimistic.remove()
      this.inputTarget.value = content
    } finally {
      this.buttonTarget.disabled = false
    }
  }

  #appendOptimisticMessage(content) {
    // Mirror the server-rendered human-teammate row (ai/conversations/_bubble → agent variant, right
    // side) so the optimistic bubble is visually identical before it swaps for the broadcast.
    const messages = document.getElementById("messages")
    const author = this.authorValue || "Team Member"
    const el = document.createElement("div")
    el.classList.add("conversation-row", "conversation-row--right")
    el.dataset.optimistic = "true"
    el.innerHTML = `
      <div class="conversation-avatar conversation-avatar--agent">${this.#escapeHtml(this.#initials(author))}</div>
      <div class="conversation-stack">
        <div class="conversation-name conversation-name--agent">${this.#escapeHtml(author)}<span class="conversation-time">${this.#formatTime()}</span></div>
        <div class="conversation-bubble conversation-bubble--agent" shadowroot="open"><p>${this.#escapeHtml(content).replace(/\n/g, "<br>")}</p></div>
      </div>
    `
    messages.appendChild(el)
    el.scrollIntoView()
    return el
  }

  #initials(text) {
    const parts = String(text).split(/[\s@._-]+/).filter(Boolean)
    const raw = parts.length >= 2 ? parts[0][0] + parts[1][0] : (parts[0] || "?").slice(0, 2)
    return raw.toUpperCase()
  }

  #formatTime() {
    const now = new Date()
    return now.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true
    })
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  #observeMessages() {
    const messages = document.getElementById("messages")
    if (!messages) return

    this.observer = new MutationObserver((mutations) => {
      const hasServerMessage = mutations.some(m =>
        [...m.addedNodes].some(n => n.nodeType === 1 && !n.dataset.optimistic)
      )
      if (hasServerMessage) {
        messages.querySelectorAll("[data-optimistic]").forEach(el => el.remove())
      }
    })
    this.observer.observe(messages, { childList: true })
  }
}
